// Process Admin – Area | Process | Subprocess (FlexibleCRUDTable + search).
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import {
  benchmarkProcessesAPI,
  type BenchmarkProcess,
  type ProcessSubprocessTableRow
} from '@/services/benchmarkProcessesAPI';
import { processAdminTableService } from '@/services/processAdminTableService';
import { processAdminEntity } from '@/config/entities/processAdmin';
import { hierarchyService, type HierarchyOption } from '@/services/hierarchyService';
import { templateService } from '@/services/templateService';
import { useAuth } from '@/state/auth/AuthProvider';
import { PlusIcon, Square2StackIcon } from '@heroicons/react/24/outline';

function toProcess(row: ProcessSubprocessTableRow): BenchmarkProcess {
  return {
    id: row.process_id,
    name: row.process_name,
    parent_id: null,
    area_name: row.area_name,
    tenant_id: null,
    created_at: '',
    updated_at: ''
  };
}

export const BenchmarkProcessAdminPage: React.FC = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [areaOptions, setAreaOptions] = useState<HierarchyOption[]>([]);
  const [modal, setModal] = useState<'process' | 'subprocess' | 'template' | null>(null);
  const [editingProcess, setEditingProcess] = useState<BenchmarkProcess | null>(null);
  const [editingSubprocessId, setEditingSubprocessId] = useState<string | null>(null);
  const [parentForSubprocess, setParentForSubprocess] = useState<BenchmarkProcess | null>(null);
  const [formName, setFormName] = useState('');
  const [formArea, setFormArea] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const loadAreaOptions = useCallback(async () => {
    try {
      const areas = await hierarchyService.getFunctionalLevel2();
      setAreaOptions(areas);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadAreaOptions();
  }, [loadAreaOptions]);

  const openAddProcess = useCallback(() => {
    setEditingProcess(null);
    setFormName('');
    setFormArea('');
    setModal('process');
  }, []);

  const openAddSubprocess = useCallback((process: BenchmarkProcess) => {
    setParentForSubprocess(process);
    setEditingSubprocessId(null);
    setFormName('');
    setModal('subprocess');
  }, []);

  const openEditProcess = useCallback((process: BenchmarkProcess) => {
    setEditingProcess(process);
    setFormName(process.name);
    setFormArea(process.area_name ?? '');
    setModal('process');
  }, []);

  const openEditSubprocess = useCallback((row: ProcessSubprocessTableRow) => {
    if (!row.subprocess_id) return;
    setParentForSubprocess(toProcess(row));
    setEditingSubprocessId(row.subprocess_id);
    setFormName(row.subprocess_name ?? '');
    setModal('subprocess');
  }, []);

  const closeModal = useCallback(() => {
    setModal(null);
    setEditingProcess(null);
    setEditingSubprocessId(null);
    setParentForSubprocess(null);
    setTemplateName('');
    setTemplateDescription('');
  }, []);

  const openSaveAsTemplate = useCallback(() => {
    setTemplateName('');
    setTemplateDescription('');
    setModal('template');
  }, []);

  const saveAsTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (selectedIds.length === 0) {
      toast.error('Select at least one row');
      return;
    }
    if (!user?.id || !user?.tenantId) {
      toast.error('You must be logged in to save templates');
      setSaving(false);
      return;
    }
    setSaving(true);
    try {
      const rows = await benchmarkProcessesAPI.listProcessSubprocessTable();
      const idSet = new Set(selectedIds);
      const selectedRows = rows.filter((row) => {
        const rowId = row.subprocess_id ?? row.process_id;
        return idSet.has(rowId);
      });
      if (selectedRows.length === 0) {
        toast.error('No processes selected');
        setSaving(false);
        return;
      }
      // Group by process_id, then by area for content shape
      const byProcess = new Map<
        string,
        { area_name: string | null; process_name: string; subprocesses: string[] }
      >();
      for (const row of selectedRows) {
        if (!byProcess.has(row.process_id)) {
          byProcess.set(row.process_id, {
            area_name: row.area_name ?? null,
            process_name: row.process_name,
            subprocesses: []
          });
        }
        const entry = byProcess.get(row.process_id)!;
        if (row.subprocess_id && row.subprocess_name) {
          entry.subprocesses.push(row.subprocess_name);
        }
      }
      const byArea: Record<string, { area_name: string | null; processes: Array<{ name: string; subprocesses: Array<{ name: string }> }> }> = {};
      for (const { area_name, process_name, subprocesses } of byProcess.values()) {
        const key = area_name ?? '';
        if (!byArea[key]) {
          byArea[key] = { area_name, processes: [] };
        }
        byArea[key].processes.push({
          name: process_name,
          subprocesses: subprocesses.map((name) => ({ name }))
        });
      }
      const process_areas = Object.values(byArea);
      const content = { process_areas };
      await templateService.createTemplate({
        tenant_id: user.tenantId,
        name: templateName.trim(),
        description: templateDescription.trim() || undefined,
        template_type: 'PROCESS_AREAS',
        content,
        created_by: user.id
      });
      toast.success(`Template "${templateName.trim()}" saved. You can find it under Templates > Manage.`);
      closeModal();
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
      toast.error('Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  const refreshTable = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const saveProcess = async () => {
    if (!formName.trim()) {
      toast.error('Process name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingProcess) {
        await benchmarkProcessesAPI.update(editingProcess.id, {
          name: formName.trim(),
          area_name: formArea || null
        });
        toast.success('Process updated');
      } else {
        await benchmarkProcessesAPI.create({
          name: formName.trim(),
          area_name: formArea || null
        });
        toast.success('Process created');
      }
      closeModal();
      refreshTable();
    } catch (e) {
      console.error(e);
      toast.error(editingProcess ? 'Failed to update process' : 'Failed to create process');
    } finally {
      setSaving(false);
    }
  };

  const saveSubprocess = async () => {
    if (!formName.trim()) {
      toast.error('Subprocess name is required');
      return;
    }
    if (!parentForSubprocess && !editingSubprocessId) {
      toast.error('Parent process is required');
      return;
    }
    setSaving(true);
    try {
      if (editingSubprocessId) {
        await benchmarkProcessesAPI.update(editingSubprocessId, { name: formName.trim() });
        toast.success('Subprocess updated');
      } else if (parentForSubprocess) {
        await benchmarkProcessesAPI.create({
          name: formName.trim(),
          parent_id: parentForSubprocess.id
        });
        toast.success('Subprocess created');
      }
      closeModal();
      refreshTable();
    } catch (e) {
      console.error(e);
      toast.error(editingSubprocessId ? 'Failed to update subprocess' : 'Failed to create subprocess');
    } finally {
      setSaving(false);
    }
  };

  const deleteProcess = useCallback(async (process: BenchmarkProcess) => {
    try {
      await benchmarkProcessesAPI.delete(process.id);
      toast.success('Deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete');
    }
  }, []);

  const deleteSubprocess = useCallback(async (subprocessId: string, _name: string) => {
    try {
      await benchmarkProcessesAPI.delete(subprocessId);
      toast.success('Deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete');
    }
  }, []);

  const handleEdit = useCallback(
    (item: ProcessSubprocessTableRow & { id: string }) => {
      if (item.subprocess_id) {
        openEditSubprocess(item);
      } else {
        openEditProcess(toProcess(item));
      }
    },
    [openEditProcess, openEditSubprocess]
  );

  const handleDelete = useCallback(
    async (item: ProcessSubprocessTableRow & { id: string }) => {
      if (item.subprocess_id) {
        await deleteSubprocess(item.subprocess_id, item.subprocess_name ?? '');
      } else {
        await deleteProcess(toProcess(item));
      }
    },
    [deleteProcess, deleteSubprocess]
  );

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-sm border border-[var(--color-border)] p-4">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-[var(--color-surface-text)]">Process Admin</h1>
              <p className="text-sm text-[var(--color-secondary-text)] mt-1">
                Define areas and processes; each process can have multiple subprocesses. Select rows and save as a template to assign to tenants.
              </p>
            </div>
            {selectedIds.length > 0 && (
              <button
                type="button"
                onClick={openSaveAsTemplate}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Square2StackIcon className="h-4 w-4 mr-2" />
                Save as template ({selectedIds.length} selected)
              </button>
            )}
          </div>

          <FlexibleCRUDTable
            key={refreshKey}
            entity={processAdminEntity}
            service={processAdminTableService}
            showSearch={true}
            searchPlaceholder="Search area, process, subprocess…"
            showCreateButton={true}
            onCreate={openAddProcess}
            onEdit={handleEdit}
            onDelete={handleDelete}
            showPagination={true}
            pageSize={25}
            onOperationComplete={refreshTable}
            enableRowSelection={true}
            onSelectionChange={setSelectedIds}
            customActions={[
              {
                label: 'Add subprocess',
                icon: PlusIcon,
                onClick: (item: ProcessSubprocessTableRow & { id: string }) =>
                  openAddSubprocess(toProcess(item))
              }
            ]}
          />
        </div>
      </div>

      {modal === 'process' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-lg border border-[var(--color-border)] w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {editingProcess ? 'Edit process' : 'Add process'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Area (from function level 2) *</label>
                <select
                  value={formArea}
                  onChange={(e) => setFormArea(e.target.value)}
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                >
                  <option value="">Select area…</option>
                  {areaOptions.map((opt) => (
                    <option key={opt.value} value={opt.label}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {areaOptions.length === 0 && (
                  <p className="text-xs text-[var(--color-secondary-text)] mt-1">No functional hierarchy level-2 nodes. Define them in Hierarchies → Functional.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Process name *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                  placeholder="e.g. Service Management"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-[var(--color-border)] rounded text-[var(--color-surface-text)]">Cancel</button>
              <button type="button" onClick={saveProcess} disabled={saving || !formName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : editingProcess ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'template' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-lg border border-[var(--color-border)] w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">Save as template</h2>
            <p className="text-sm text-[var(--color-secondary-text)] mb-4">
              Selected rows will be saved as a template. Each selected process (and its subprocesses) will be included. You can assign this template to tenants later.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Template name *</label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                  placeholder="e.g. Customer Service processes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Description (optional)</label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                  placeholder="Optional description"
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-[var(--color-border)] rounded text-[var(--color-surface-text)]">Cancel</button>
              <button type="button" onClick={saveAsTemplate} disabled={saving || !templateName.trim()} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Create template'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'subprocess' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-lg border border-[var(--color-border)] w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
              {editingSubprocessId ? 'Edit subprocess' : 'Add subprocess'}
            </h2>
            {parentForSubprocess && !editingSubprocessId && (
              <p className="text-sm text-[var(--color-secondary-text)] mb-2">
                Under process: <strong>{parentForSubprocess.name}</strong>
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-1">Subprocess name *</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="w-full border border-[var(--color-border)] rounded px-3 py-2 bg-[var(--color-secondary-bg)] text-[var(--color-surface-text)]"
                placeholder="e.g. Incident Management"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={closeModal} className="px-4 py-2 border border-[var(--color-border)] rounded text-[var(--color-surface-text)]">Cancel</button>
              <button type="button" onClick={saveSubprocess} disabled={saving || !formName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : editingSubprocessId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
